/**
 * Sidebar - 280px left rail for /chat post-login (Gemini-style).
 *
 * MD-31 ASKLAW-EMBER-THEME Phase 2. Per spec F-FE-THEME-GEMINI §6.3 + §7.2 + AC-06.
 *
 * Sections (top -> bottom):
 *   1. "+ Cuoc tro chuyen moi" filled pill gradient (width 100% - margin 16px)
 *   2. Search input pill voi icon Search
 *   3. Section "Gan day" (Recent) - render ConversationItem list max 20
 *   4. Library + Notebooks - HIDDEN Phase 2-3, show with "Sap ra mat" tooltip Phase 4+
 *   5. Footer: legal links only (PDPA). Account button + menu chuyen sang
 *      AccountPopover tren NavRail (app shell spec 2026-06-12-telegram-liquid-glass)
 *
 * Layout modes:
 *   - Desktop (>= 1024px): fixed 280px rail, always visible
 *   - Mobile (< 1024px): drawer slide-right 280px, backdrop overlay 50% black blur,
 *     toggle via isOpen + onClose props
 *
 * Animation: sidebar slide-in from left 280px (320ms power3.out) - orchestrated by
 * use-gsap-chat-orchestrate.ts on mount. Reduced-motion -> instant.
 *
 * Reuses existing helpers from ChatHistory: loadAnonThreadList + deleteAnonThread.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlusIcon, SearchIcon } from './icons';
import { ConversationItem } from './ConversationItem';
import {
  loadAnonThreadList,
  deleteAnonThread,
  type SidebarMessage,
  type SidebarSession,
} from './ChatHistory';

export type SidebarProps = {
  lang: 'vi' | 'en';
  isAuthed: boolean;
  accessToken: string | null;
  currentSessionId: string | null;
  /** Bump to force reload (after persisting a new message). */
  refreshTick?: number;
  /** Desktop mode: always visible. Mobile drawer: respect isOpen. */
  variant?: 'rail' | 'drawer';
  isOpen?: boolean;
  onClose?: () => void;
  onNewChat: () => void;
  onSelectSession: (sid: string, messages: SidebarMessage[]) => void;
  /** Phase 1B (spec 2026-06-15 muc 13.4 B6): anon quota chip o footer history
      pane - chuyen tu duoi composer ra day. Chi hien khi isAnon. */
  isAnon?: boolean;
  anonTurnCount?: number;
  anonCap?: number;
};

const RECENT_MAX = 20;

export function Sidebar({
  lang,
  isAuthed,
  accessToken,
  currentSessionId,
  refreshTick = 0,
  variant = 'rail',
  isOpen = true,
  onClose,
  onNewChat,
  onSelectSession,
  isAnon = false,
  anonTurnCount = 0,
  anonCap = 20,
}: SidebarProps) {
  const isDrawer = variant === 'drawer';
  const visible = !isDrawer || isOpen;

  const [sessions, setSessions] = useState<SidebarSession[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    setError(null);
    if (!isAuthed) {
      const threads = loadAnonThreadList();
      setSessions(
        threads.map((t) => ({
          session_id: t.id,
          title: t.title,
          last_turn_at: t.last_message_at,
          turn_count: t.messages.filter((m) => m.role === 'user').length,
          created_at: t.created_at,
        }))
      );
      return;
    }
    if (!accessToken) {
      setSessions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/v1/me/chat?limit=50', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        setSessions([]);
        return;
      }
      const data = (await res.json()) as { sessions: SidebarSession[] };
      setSessions(data.sessions ?? []);
    } catch (e) {
      setError((e as Error).message);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthed, accessToken]);

  useEffect(() => {
    if (isDrawer && !isOpen) return;
    loadList();
  }, [loadList, refreshTick, isDrawer, isOpen]);

  // ESC closes drawer.
  useEffect(() => {
    if (!isDrawer || !isOpen || !onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDrawer, isOpen, onClose]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const list = needle
      ? sessions.filter((s) => (s.title ?? '').toLowerCase().includes(needle))
      : sessions;
    return list.slice(0, RECENT_MAX);
  }, [sessions, search]);

  const handleSelect = useCallback(
    async (sid: string) => {
      if (!isAuthed) {
        const threads = loadAnonThreadList();
        const t = threads.find((x) => x.id === sid);
        onSelectSession(sid, t?.messages ?? []);
        return;
      }
      if (!accessToken) return;
      try {
        const res = await fetch(`/api/v1/me/chat/${encodeURIComponent(sid)}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) {
          setError(`load HTTP ${res.status}`);
          return;
        }
        const data = (await res.json()) as { messages: SidebarMessage[] };
        onSelectSession(sid, data.messages ?? []);
        if (isDrawer && onClose) onClose();
      } catch (e) {
        setError((e as Error).message);
      }
    },
    [isAuthed, accessToken, onSelectSession, isDrawer, onClose]
  );

  const handleDelete = useCallback(
    async (sid: string) => {
      const ok = window.confirm(
        lang === 'vi' ? 'Xoá cuộc trò chuyện này?' : 'Delete this conversation?'
      );
      if (!ok) return;
      if (!isAuthed) {
        deleteAnonThread(sid);
        await loadList();
        if (sid === currentSessionId) onNewChat();
        return;
      }
      if (!accessToken) return;
      try {
        const res = await fetch(`/api/v1/me/chat/${encodeURIComponent(sid)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (res.ok) {
          await loadList();
          if (sid === currentSessionId) onNewChat();
        } else {
          setError(`delete HTTP ${res.status}`);
        }
      } catch (e) {
        setError((e as Error).message);
      }
    },
    [isAuthed, accessToken, lang, loadList, currentSessionId, onNewChat]
  );

  if (!visible) return null;

  return (
    <>
      {isDrawer && (
        <div
          className="chat-sidebar__backdrop"
          aria-hidden="true"
          onClick={onClose}
        />
      )}
      <aside
        className={[
          'chat-sidebar',
          isDrawer ? 'chat-sidebar--drawer' : 'chat-sidebar--rail',
        ].join(' ')}
        data-chat-sidebar
        role="navigation"
        aria-label={lang === 'vi' ? 'Lịch sử trò chuyện' : 'Chat history'}
      >
        <div className="chat-sidebar__top">
          <button
            type="button"
            className="chat-sidebar__new"
            onClick={() => {
              onNewChat();
              if (isDrawer && onClose) onClose();
            }}
          >
            <PlusIcon size={16} strokeWidth={2} />
            <span>{lang === 'vi' ? 'Cuộc trò chuyện mới' : 'New chat'}</span>
          </button>
        </div>

        <div className="chat-sidebar__search-wrap">
          <SearchIcon
            size={16}
            strokeWidth={1.6}
            className="chat-sidebar__search-icon"
          />
          <input
            type="search"
            className="chat-sidebar__search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={lang === 'vi' ? 'Tìm trong lịch sử' : 'Search history'}
            aria-label={
              lang === 'vi' ? 'Tìm cuộc trò chuyện' : 'Search chats'
            }
          />
        </div>

        <div className="chat-sidebar__scroll">
          <section className="chat-sidebar-section">
            <h3 className="chat-sidebar-section__title">
              {lang === 'vi' ? 'Gần đây' : 'Recent'}
            </h3>
            {loading && (
              <div className="chat-sidebar-section__hint">
                {lang === 'vi' ? 'Đang tải...' : 'Loading...'}
              </div>
            )}
            {error && !loading && (
              <div className="chat-sidebar-section__hint chat-sidebar-section__hint--error">
                {error}
              </div>
            )}
            {!loading && filtered.length === 0 && !error && (
              <div className="chat-sidebar-section__hint">
                {lang === 'vi'
                  ? 'Chưa có cuộc trò chuyện nào.'
                  : 'No conversations yet.'}
              </div>
            )}
            <div className="chat-sidebar-section__list">
              {filtered.map((s) => (
                <ConversationItem
                  key={s.session_id}
                  id={s.session_id}
                  title={
                    s.title ||
                    (lang === 'vi' ? 'Cuộc trò chuyện' : 'Conversation')
                  }
                  lastMessageAt={s.last_turn_at}
                  isActive={s.session_id === currentSessionId}
                  lang={lang}
                  onSelect={handleSelect}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </section>

          {/* Library + Notebooks hidden Phase 2-3 per AC-06 (Stage C). KHONG render at all to avoid clutter. */}
        </div>

        <div className="chat-sidebar__footer">
          {/* Account button + menu chuyen sang AccountPopover tren NavRail
              (app shell spec 2026-06-12-telegram-liquid-glass). */}
          {/* Phase 1B (spec 2026-06-15 muc 13.4 B6): anon quota chuyen tu duoi
              composer ra day. Chi la hint tien ich - KHONG phai disclaimer phap
              ly (disclaimer giu nguyen tren composer per HARD VETO #4). */}
          {isAnon && (
            <div className="chat-sidebar__quota" role="status">
              {lang === 'vi'
                ? `Ẩn danh ${anonTurnCount}/${anonCap} lượt`
                : `Anonymous ${anonTurnCount}/${anonCap} questions`}
            </div>
          )}
          {/* PDPA reachability (legal-upl C3): the global Footer is route-gated
              off /chat, so the privacy policy, terms, and DPO contact MUST stay
              reachable from the chat shell. These small persistent links keep
              /privacy + /terms + the DPO mailto in reach without the Footer. */}
          <nav
            className="chat-sidebar__legal"
            aria-label={lang === 'vi' ? 'Liên kết pháp lý' : 'Legal links'}
          >
            <a className="chat-sidebar__legal-link" href="/privacy">
              {lang === 'vi' ? 'Quyền riêng tư' : 'Privacy'}
            </a>
            <span className="chat-sidebar__legal-sep" aria-hidden="true">
              |
            </span>
            <a className="chat-sidebar__legal-link" href="/terms">
              {lang === 'vi' ? 'Điều khoản' : 'Terms'}
            </a>
            <span className="chat-sidebar__legal-sep" aria-hidden="true">
              |
            </span>
            <a className="chat-sidebar__legal-link" href="mailto:dpo@asklaw.vn">
              DPO
            </a>
          </nav>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
